import Component from '@ember/component';
import { action, computed, get } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty, isEqual } from '@ember/utils';
import RSVP from 'rsvp';

/*
  attrs
    - selectedContactId (optional)
    - placeholder (optional) (default: 'Choose contact')
    - focusOnInsert - if the input box should receive focus when it's inserted
    - disabled
    - showAsInvalid
      - if true, then outlines the select box in red
    - showBlankContact
      - Whether to show a blank contact
      - This allows the user to 'unselect' a selected contact
      - default is false
    - blankContactLabel
      - Label for blank contact
      - default is 'None'
    - offerToCreateNewContact (default is false)
      - if an exact match isn't found, offer to create the contact
    - triggerClass (optional)
      - the class to add to the trigger element
    - onclose (optional)
      - the action to call when the power select is closed
    - onSelection
      - action that is fired when a user makes a selection, even if it's the same option
*/

export default Component.extend({
  contactsCache: service(),
  classNames: ['select-contact'],
  classNameBindings: ['showAsInvalid'],

  contacts: alias('contactsCache.sortedContacts'),

  selectedContactId: null,
  selectedContact: computed('selectedContactId', function () {
    return this.contactsCache.getById(this.selectedContactId);
  }),
  placeholder: 'Choose contact',
  showBlankContact: false,
  blankContactLabel: 'None',
  disabled: false,
  triggerClass: '',
  initialContactsToShow: 20,
  offerToCreateNewContact: false,
  showAsInvalid: false,

  initialContacts: computed(
    'contacts.[]',
    'showBlankContact',
    'initialContactsToShow',
    function () {
      let contacts = this.contacts;
      let initialContacts = [];
      if (this.showBlankContact) {
        initialContacts.push({
          type: 'blank',
        });
      }
      initialContacts = initialContacts.concat(
        contacts.slice(0, this.initialContactsToShow),
      );
      if (contacts.length > initialContacts.length) {
        initialContacts.push({
          type: 'more',
        });
      }
      return initialContacts;
    },
  ),

  didInsertElement: function () {
    this._super(...arguments);
    if (this.focusOnInsert) {
      setTimeout(() => {
        this.element.querySelector('.ember-power-select-trigger').focus();
      }, 50);
    }
  },

  focusComesFromOutside(e) {
    if (e.target.classList.contains('ember-basic-dropdown-trigger--below')) {
      return false;
    }
    let blurredEl = e.relatedTarget;
    return (
      isEmpty(blurredEl) ||
      !blurredEl.classList.contains('ember-power-select-search-input')
    );
  },

  // TODO: Search on multiple words
  searchContacts: action(function (term) {
    let contacts = this.contacts;
    if (isEmpty(term)) {
      return this.initialContacts;
    }
    let termLowerCase = term.trim().toLowerCase();
    let filteredContacts = contacts.filter((contact) => {
      return get(contact, 'nameLowerCase').indexOf(termLowerCase) >= 0;
    });

    // If there isn't an exact match, then give the option to create a new contact
    if (
      this.offerToCreateNewContact &&
      filteredContacts.find((contact) => {
        return get(contact, 'nameLowerCase') === termLowerCase;
      }) == null
    ) {
      filteredContacts.push({
        type: 'create',
        name: `Create '${term}' as a new contact`,
      });
    }

    return filteredContacts;
  }),

  handleFocused: action(function (select, e) {
    if (!e.target.classList.contains('ember-power-select-trigger')) {
      return;
    }
    /* TODO: implement this
    let focusingOnField = get(this, 'focusingOnField');
    if (focusingOnField != null && !e.target.classList.contains(`field-${focusingOnField}`)) {
      return;
    }
    */
    if (this.focusComesFromOutside(e)) {
      setTimeout(() => {
        select.actions.open();
      }, 0);
    }
  }),

  handleClosed: action(function () {
    if (this.onclose) {
      this.onclose();
    }
  }),

  handleBuildSelection: action(function (option, select) {
    if (isEqual(option, select.selected)) {
      if (this.onSelection) {
        this.onSelection(this.selectedContactId, { newContact: false });
      }
    }
    return option;
  }),

  selectContact: action(function (contact, select) {
    let contactType = get(contact, 'type');
    if (contactType === 'more') {
      // do nothing
      return;
    }
    let contactId = get(contact, 'id') || null;
    let newContact = false;
    let createContactPromise = RSVP.resolve();
    if (contactType === 'create') {
      let name = get(contact, 'name').match(
        /^Create '(.*)' as a new contact$/,
      )[1];
      let newContact = this.contactsCache.createContact({ name: name });
      createContactPromise = newContact.save().then(() => {
        contactId = get(newContact, 'id');
        newContact = true;
      });
    }

    createContactPromise.then(() => {
      let onSelection = this.onSelection;
      if (onSelection) {
        onSelection(contactId, { newContact: newContact });
      }
      setTimeout(() => {
        select.actions.close();
      }, 0);
    });
  }),
});
